import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import {
  FormControl,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Grid,
  Button,
  FormHelperText,
  Box,
  CircularProgress,
  Checkbox,
  Alert,
  Snackbar,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  useAppDispatch,
  useAppSelector,
  useInsuranceRisk,
} from 'src/utils/hooks';
import { startRiskCheckAdditional } from 'src/store/thunks/riskCheck.thunk';
import {
  addQuestionaire,
  setOpenNoticeFalse,
  setOpenNoticeTrue,
} from 'src/store/reducers/riskCheck.reducer';
import { decrementStep } from 'src/store/reducers/stepper.reducers';
import DynamicForm from './DynamicForm';
import { useEffect, useRef, useState } from 'react';
import Person from 'src/components/Person';
import { PickersDayProps } from '@mui/x-date-pickers';
import { PickersDay } from '@mui/x-date-pickers';
import CorrectInformationOnly from './CorrectInfromationOnly';
import { saveTitle } from 'src/store/reducers/contribution.reducer';
import { setGewerkschaftsnummer } from 'src/store/reducers/config.reducer';
import specialHint from 'src/config/specialHint.json';
import ReactGA from 'react-ga4';

import 'dayjs/locale/de'; // Import the German locale for dayjs
import { findHints } from 'src/utils';
import TooltipReusableWhiteBg from 'src/components/TooltipReusableWhiteBg';

dayjs.locale('de');

interface Props {
  bithdate: string;
  gender: string;
  index: number;
}
export default function Personscomp({ bithdate, gender, index }: Props) {
  const [year, month, day] = bithdate.split('-');
  const formattedBirthdate = `${day}.${month}.${year}`;

  localStorage.setItem('birthdate', formattedBirthdate);

  const personRef = useRef<null | HTMLDivElement>(null);
  const { isDownloading } = useAppSelector((state) => state.riskCheck);
  const { disableDataProtectionBox } = useAppSelector((state) => state.config);
  const { openNotice } = useAppSelector(
    (state) => state.riskCheck.selectedInsurences[index - 1]
  );
  const { isColorBlindMode } = useAppSelector((state) => state.config);
  const riskIdxState = useAppSelector(
    (state) => state.riskCheck.selectedInsurences[index - 1].riskCheckIdx
  );
  const { askForGewerkschaft, mandant } = useAppSelector((state) => state.config);
  const showGewerkschaft = askForGewerkschaft && index === 1 && mandant === 'sdk';
  const [insuranceRiskArray, person, riskIdx, options] = useInsuranceRisk(
    index,
    riskIdxState
  );
  const insuranceRisk = insuranceRiskArray[riskIdx];

  const validationSchema =
    mandant === 'hallesche'
      ? yup.object({
          firstName: yup
            .string()
            .matches(
              /^(?=(?:.*?[^\s]){2,})[A-Za-zÀ-ÖØ-öø-ÿ0-9/,.&\- ]{1,25}$/,
              'Vorname muss mindestens 2 Zeichen (ohne Leerzeichen) und maximal 25 Zeichen lang sein. Erlaubte Sonderzeichen: / - , . &',
            )
            .required('Eingabe erforderlich.'),
          lastName: yup
            .string()
            .matches(
              /^(?=(?:.*?[^\s]){2,})[A-Za-zÀ-ÖØ-öø-ÿ0-9/,.&\- ]{1,28}$/,
              'Nachname muss mindestens 2 Zeichen (ohne Leerzeichen) und maximal 28 Zeichen lang sein. Erlaubte Sonderzeichen: / - , . &',
            )
            .required('Eingabe erforderlich.'),
          acknowledge: disableDataProtectionBox
            ? yup.boolean()
            : yup
                .boolean()
                .oneOf([true], 'Bitte bestätigen.')
                .required('Bitte bestätigen.'),
          gewerkschaftsnummer: showGewerkschaft
            ? yup.string().trim().required('Eingabe erforderlich.')
            : yup.string(),
        })
      : yup.object({
          firstName: yup.string().trim().required('Eingabe erforderlich.'),
          lastName: yup.string().trim().required('Eingabe erforderlich.'),
          acknowledge: disableDataProtectionBox
            ? yup.boolean()
            : yup
                .boolean()
                .oneOf([true], 'Bitte bestätigen.')
                .required('Bitte bestätigen.'),
          gewerkschaftsnummer: showGewerkschaft
            ? yup.string().trim().required('Eingabe erforderlich.')
            : yup.string(),
        });

  const dispatch = useAppDispatch();
  const formik = useFormik({
    initialValues: {
      firstName:
        insuranceRisk?.questionnaire?.questionaryValues
          ?.find(({ key }) => key === 'firstName')
          ?.value.trim() ?? person.firstName,
      lastName:
        insuranceRisk?.questionnaire?.questionaryValues
          ?.find(({ key }) => key === 'familyName')
          ?.value.trim() ?? person.lastName,
      acknowledge: false,
      gewerkschaftsnummer: '',
    },
    validationSchema: validationSchema,
    validateOnMount: true,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (values) => {
      const trimmedValues = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        acknowledge: values.acknowledge,
      };

      if (insuranceRisk?.questionnaire) {
        dispatch(setOpenNoticeFalse({ personIdx: index - 1 }));
      } else {
        dispatch(
          addQuestionaire({ personIdx: index - 1, values: trimmedValues })
        );
        if (process.env.REACT_APP_ENV === 'production') {
          ReactGA.event({
            category: 'events',
            action: `S:${insuranceRisk.option.symbol}-${insuranceRisk.option.displayString}`,
          });
        }
        dispatch(
          saveTitle({
            personId: person.Id,
            title: `(${trimmedValues.firstName} ${
              trimmedValues.lastName
            }, geboren am ${dayjs(bithdate).format('DD.MM.YYYY')})`,
            firstName: trimmedValues.firstName,
            lastName: trimmedValues.lastName,
          })
        );
        if (showGewerkschaft && values.gewerkschaftsnummer) {
          dispatch(setGewerkschaftsnummer(values.gewerkschaftsnummer.trim()));
        }
        dispatch(
          startRiskCheckAdditional({
            Id: person.Id,
            riskIdx: riskIdx,
            personIdx: index - 1,
            values: {
              firstName: trimmedValues.firstName,
              familyName: trimmedValues.lastName,
            },
          } as RiskCheckThunkProps)
        );
      }

      formik.setValues({ ...trimmedValues, gewerkschaftsnummer: values.gewerkschaftsnummer }, false);
    },
  });

  useEffect(() => {
    if (insuranceRisk?.moreQuestions) {
      personRef.current?.scrollIntoView();
    }
  }, [insuranceRisk?.moreQuestions]);

  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    let timer: any;
    if (insuranceRisk?.loading || isDownloading) {
      timer = setTimeout(() => {
        setShowToast(true);
      }, 30000);
    } else {
      clearTimeout(timer);
      setShowToast(false);
    }
    return () => clearTimeout(timer);
  }, [insuranceRisk?.loading, isDownloading]);

  const [value, setValue] = useState<Dayjs | null>(dayjs(bithdate));
  const name = [formik.values.firstName, formik.values.lastName]
    .filter((v) => v)
    .join(' ');

  const handleDateChange = (newValue: Dayjs | null) => {
    setValue(newValue);
  };

  const renderCustomDay = (
    day: Dayjs,
    _: Dayjs[],
    pickersDayProps: PickersDayProps<Dayjs>
  ) => {
    const { children, ...restProps } = pickersDayProps;
    return (
      <PickersDay
        {...restProps}
        sx={{ fontSize: '13px' }}
        onClick={() => handleDateChange(day)}
      >
        {children}
      </PickersDay>
    );
  };

  const hintsArray: SpecialHint[] = findHints(specialHint, insuranceRisk);
  let showHint: SpecialHint | undefined = hintsArray.find(
    (hint) => hint.tariffId === insuranceRisk.option.tarrifId
  );
  if (!showHint && hintsArray.length > 0) {
    showHint = hintsArray[0];
  }

  return (
    <Person
      index={index}
      title={`(${name ? name + ', ' : ''} geboren am ${dayjs(bithdate).format(
        'DD.MM.YYYY'
      )})`}
      personRef={personRef}
      options={options}
      defaultExpanded={false}
      riskIndex={riskIdx + 1}
      showCompletedTariffs={false}
    >
      <>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} paddingY={3} sx={{ paddingBottom: 0 }}>
            {openNotice && <Grid xs={12} paddingLeft={2}></Grid>}

            {showGewerkschaft && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    id="gewerkschaftsnummer"
                    label="IGBCE Gewerkschaftsmitgliedsnummer"
                    variant="filled"
                    required
                    disabled={!!insuranceRisk?.questionnaire}
                    {...formik.getFieldProps('gewerkschaftsnummer')}
                    error={
                      formik.touched.gewerkschaftsnummer &&
                      Boolean(formik.errors.gewerkschaftsnummer)
                    }
                    helperText={
                      formik.touched.gewerkschaftsnummer &&
                      formik.errors.gewerkschaftsnummer
                    }
                  />
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl>
                <RadioGroup
                  row
                  defaultValue={gender.toUpperCase()}
                  name="Gender Radio"
                >
                  <FormControlLabel
                    value="MALE"
                    disabled
                    control={
                      <Radio
                        sx={{
                          color: '#747474',
                          '&.Mui-checked': {
                            color: '#747474',
                          },
                        }}
                      />
                    }
                    sx={{
                      '& .MuiSvgIcon-root': {
                        fontSize: 24,
                      },
                      '&.Mui-disabled .MuiTypography-root': {
                        color: '#747474',
                      },
                    }}
                    label="Männlich"
                  />
                  <FormControlLabel
                    value="FEMALE"
                    disabled
                    sx={{
                      '& .MuiSvgIcon-root': {
                        fontSize: 24,
                      },
                      '&.Mui-disabled .MuiTypography-root': {
                        color: '#747474',
                      },
                    }}
                    control={
                      <Radio
                        sx={{
                          color: '#747474',
                          '&.Mui-checked': {
                            color: '#747474',
                          },
                          '&.Mui-disabled': {
                            color: '#747474',
                          },
                        }}
                      />
                    }
                    label="Weiblich"
                  />
                </RadioGroup>
                <FormHelperText
                  sx={{
                    margin: 0,
                    color: '#747474',
                  }}
                >
                  {isColorBlindMode
                    ? 'Wenn Sie etwas ändern wollen, klicken Sie bitte unten auf die Schalt-Fläche „ZURÜCK“'
                    : 'Für Änderung bitte einen Schritt zurück.'}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid
              container
              spacing={2}
              marginY={1}
              marginX={0}
              sx={{
                paddingBottom: 2.5,
              }}
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Grid item xs={12} sm={6} md={4} sx={{ borderBottom: 'none' }}>
                <FormControl fullWidth>
                  <TextField
                    id="standard-basic"
                    label="Vorname"
                    variant="filled"
                    disabled={!!insuranceRisk?.questionnaire}
                    {...formik.getFieldProps('firstName')}
                    error={
                      formik.touched.firstName &&
                      Boolean(formik.errors.firstName)
                    }
                    helperText={
                      formik.touched.firstName && formik.errors.firstName
                    }
                    sx={{
                      '& .MuiInputLabel-root.Mui-disabled': {
                        color: '#747474 !important',
                      },
                      '&:hover .MuiInputLabel-root.Mui-disabled': {
                        color: '#747474 !important',
                      },
                      '& .Mui-disabled': {
                        backgroundColor: isColorBlindMode
                          ? 'transparent !important'
                          : 'inputFields.bgColor !important',
                      },
                      ...(isColorBlindMode && {
                        '& .MuiInputLabel-root': {
                          color: 'black',
                        },
                        '& .MuiFilledInput-root': {
                          backgroundColor: '#FFF',
                        },
                      }),
                      ...(!!insuranceRisk?.questionnaire
                        ? {
                            // If disabled: remove underline
                            '& .MuiFilledInput-root:before': {
                              borderBottom: 'none',
                            },
                            '& .MuiFilledInput-root:after': {
                              borderBottom: 'none',
                            },
                          }
                        : {
                            // If enabled & isColorBlindMode: make underline black
                            ...(isColorBlindMode && {
                              '& .MuiFilledInput-root:before': {
                                borderBottom: '1px solid #000',
                              },
                              '& .MuiFilledInput-root:after': {
                                borderBottom: '1px solid #000',
                              },
                            }),
                          }),
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <TextField
                    id="standard-basic"
                    label="Nachname"
                    variant="filled"
                    disabled={!!insuranceRisk?.questionnaire}
                    {...formik.getFieldProps('lastName')}
                    error={
                      formik.touched.lastName && Boolean(formik.errors.lastName)
                    }
                    helperText={
                      formik.touched.lastName && formik.errors.lastName
                    }
                    sx={{
                      '& .MuiInputLabel-root.Mui-disabled': {
                        color: '#747474 !important',
                      },
                      '&:hover .MuiInputLabel-root.Mui-disabled': {
                        color: '#747474 !important',
                      },
                      '& .Mui-disabled': {
                        backgroundColor: isColorBlindMode
                          ? 'transparent !important'
                          : 'inputFields.bgColor !important',
                      },
                      ...(isColorBlindMode && {
                        '& .MuiInputLabel-root': {
                          color: 'black',
                        },
                        '& .MuiFilledInput-root': {
                          backgroundColor: '#FFF',
                        },
                      }),
                      ...(!!insuranceRisk?.questionnaire
                        ? {
                            // If disabled: remove underline
                            '& .MuiFilledInput-root:before': {
                              borderBottom: 'none',
                            },
                            '& .MuiFilledInput-root:after': {
                              borderBottom: 'none',
                            },
                          }
                        : {
                            // If enabled & isColorBlindMode: make underline black
                            ...(isColorBlindMode && {
                              '& .MuiFilledInput-root:before': {
                                borderBottom: '1px solid #000',
                              },
                              '& .MuiFilledInput-root:after': {
                                borderBottom: '1px solid #000',
                              },
                            }),
                          }),
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Geburtsdatum"
                      inputFormat="DD.MM.YYYY"
                      disabled
                      value={value}
                      onChange={handleDateChange}
                      components={{
                        OpenPickerIcon: () => null,
                      }}
                      renderInput={(params) => (
                        <TextField
                          variant="filled"
                          disabled
                          {...params}
                          sx={{
                            backgroundColor: 'inputFields.bgColor',

                            input: {
                              color: '#000000',
                            },
                            '.MuiInputBase-root': {
                              backgroundColor: 'inputFields.bgColor',
                            },
                            '.MuiSvgIcon-root': {
                              color: '#000000',
                            },
                            '& .MuiFilledInput-root:before': {
                              borderBottom: '1px solid #000000',
                            },
                            '& .MuiFilledInput-root:after': {
                              borderBottom: '1px solid #000000',
                            },
                            '& .Mui-disabled': {
                              backgroundColor: isColorBlindMode
                                ? 'transparent !important'
                                : 'inputFields.bgColor !important',
                            },
                            label: {
                              color: '#000000',
                            },
                            ...((isColorBlindMode || params.disabled) && {
                              '& .MuiFilledInput-root:before': {
                                borderBottom: 'none',
                              },
                              '& .MuiFilledInput-root:after': {
                                borderBottom: 'none',
                              },
                            }),
                          }}
                        />
                      )}
                      renderDay={renderCustomDay}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>
              {openNotice && showHint !== undefined && (
                <Grid xs={12} paddingLeft={2} paddingTop={4}>
                  <Alert
                    severity="warning"
                    sx={{ width: '100%', alignItems: 'center' }}
                  >
                    {showHint?.specialHint}
                  </Alert>
                </Grid>
              )}
              {openNotice && !disableDataProtectionBox && (
                <Grid xs={12} paddingLeft={2} paddingTop={4}>
                  <Alert
                    severity="info"
                    icon={false}
                    sx={{
                      width: '100%',
                      alignItems: 'center',

                      ...(isColorBlindMode && {
                        backgroundColor: '#FFF',
                        color: 'black',
                        borderRadius: '8px',
                        border: '1px solid #000000',
                        '& .MuiAlert-icon': {
                          color: 'black',
                        },
                      }),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControl>
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...formik.getFieldProps('acknowledge')}
                              sx={{
                                '& .MuiSvgIcon-root': {
                                  fontSize: '23px',
                                },
                              }}
                            />
                          }
                          label={<CorrectInformationOnly />}
                        />
                      </FormControl>
                      <TooltipReusableWhiteBg
                        isHeader={true}
                        iconFlag={true}
                        width={299}
                        height={211}
                        isBlackbg={true}
                        titleStyles={{
                          fontFamily: 'Helvetica Neue',
                          fontWeight: 400,
                          fontStyle: 'normal',
                          fontSize: '14px',
                          lineHeight: '1.2',
                          letterSpacing: '0px',
                          color: '#0066B3',
                        }}
                        title={[
                          `Träger der gesetzlichen Krankenversicherung sind die Stellen, die sich um die gesetzliche Krankenversicherung kümmern.
Anspruch auf Familienversicherung bedeutet, dass man kostenlos mit in der gesetzlichen Krankenversicherung von einem Familienmitglied sein darf.
Anspruch auf freie Heilfürsorge bedeutet, dass man medizinische Hilfe kostenlos von seinem Arbeitgeber bekommt.`,
                        ]}
                      />
                    </Box>
                    {formik.touched.acknowledge &&
                      formik.errors.acknowledge && (
                        <FormHelperText error>
                          {formik.errors.acknowledge}
                        </FormHelperText>
                      )}
                  </Alert>
                </Grid>
              )}
              <Grid
                item
                xs={12}
                display={
                  insuranceRisk?.questionnaire && !openNotice ? 'none' : 'flex'
                }
                flexDirection={'row-reverse'}
                justifyContent={'space-between'}
              >
                <FormControl sx={{ width: '200px' }}>
                  <Button
                    variant="contained"
                    size="large"
                    type="submit"
                    disabled={
                      !formik.isValid || showHint?.blocksNextStep
                        ? showHint?.blocksNextStep
                        : false
                    }
                    sx={
                      isColorBlindMode
                        ? {
                            px: 0,
                            backgroundColor: 'accessibility.button.main',
                            color: 'accessibility.button.contrastText',
                            textDecoration: 'underline',
                            textUnderlineOffset: '2.5px',
                            border: (theme) =>
                              `1px solid ${theme.palette.accessibility?.button?.border}`,
                            '&:hover': {
                              backgroundColor: '#222',
                              color: 'accessibility.button.contrastText',
                              textDecoration: 'underline',
                            },
                          }
                        : { px: 0 }
                    }
                  >
                    BEANTRAGUNG STARTEN
                  </Button>
                </FormControl>
                <FormControl>
                  <Button
                    variant="contained"
                    onClick={() => {
                      dispatch(setOpenNoticeTrue({ personIdx: index - 1 }));
                      dispatch(decrementStep());
                    }}
                    sx={
                      isColorBlindMode
                        ? {
                            backgroundColor: 'accessibility.button.main',
                            color: 'accessibility.button.contrastText',
                            textDecoration: 'underline',
                            textUnderlineOffset: '2.5px',
                            border: (theme) =>
                              `1px solid ${theme.palette.accessibility?.button?.border}`,
                            '&:hover': {
                              backgroundColor: '#222',
                              color: 'accessibility.button.contrastText',
                              textDecoration: 'underline',
                            },
                          }
                        : {}
                    }
                  >
                    Zurück
                  </Button>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </form>
        {showToast && (
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={showToast}
          >
            <Alert
              severity="warning"
              sx={{
                width: '100%',
                alignItems: 'center',
                marginY: 1,
                backgroundColor: 'statusColors.alert.bgColor',
                ...(isColorBlindMode && {
                  border: '1px solid black',
                  '& .MuiAlert-icon': { color: 'black' },
                  '& .MuiAlert-message': { color: 'black' },
                }),
              }}
            >
              Wir haben aktuell Probleme mit der Anbindung festgestellt,
              versuchen aber, diese zu lösen. Bitte haben Sie einen Moment
              Geduld.
            </Alert>
          </Snackbar>
        )}
        {insuranceRisk?.loading || isDownloading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          insuranceRisk?.questionnaire &&
          !openNotice && (
            <DynamicForm
              questionaire={insuranceRisk.questionnaire}
              submitText="Weiter"
              readonly={false}
              personId={person.Id}
              personIdx={index}
              riskIdx={riskIdx}
            />
          )
        )}
      </>
    </Person>
  );
}
